import { Baseline, Highlighter, PaintBucket, SquareDashed } from 'lucide-react';
import React from 'react';

import { SkuAnnotationType, type SkuAnnotation } from '@rta/rta-blueprint-library';

import { AnnotationColorPopover } from './annotation-color-popover';
import {
	DEFAULT_ANNOTATION_FILL,
	DEFAULT_ANNOTATION_INK,
	getAnnotationCapabilities,
	isAnnotationFilled,
	isAnnotationStroked,
	setAnnotationFill,
	setAnnotationStroke,
	type AnnotationPreviewPatch,
	type EffectiveTextStyle,
	type StyleRunPatch
} from '../helpers';
import { TOOLBAR_ICON_SIZE } from './toolbar-styles';
import { useSkuAnnotation } from '../sku-annotation-context';

/** The four paint channels an annotation has. Each is "a colour, or none" -- one control, four descriptors. */
export type AnnotationPaintChannel = 'text' | 'highlight' | 'fill' | 'stroke';

type AnnotationPaintReadParams = {
	annotation: SkuAnnotation;
	/** Run-level style in force for the current text selection; only the text and highlight channels read it */
	style: EffectiveTextStyle;
};

type AnnotationPaintWriteParams = {
	annotation: SkuAnnotation;
	/** `null` is the none swatch */
	color: string | null;
	applyPatch: (patch: StyleRunPatch) => void;
};

type AnnotationPaintChannelDescriptor = {
	label: string;
	title: (annotation: SkuAnnotation) => string;
	icon: React.ReactNode;
	/** Caption for the none swatch; absent means the channel cannot be turned off */
	noneLabel?: (annotation: SkuAnnotation) => string;
	/** The live-preview patch this channel travels under; absent means the channel has no preview */
	preview?: (color: string) => AnnotationPreviewPatch;
	read: (params: AnnotationPaintReadParams) => string | null;
	write: (params: AnnotationPaintWriteParams) => void;
};

const ANNOTATION_PAINT_CHANNELS: Record<AnnotationPaintChannel, AnnotationPaintChannelDescriptor> = {
	// No none swatch: a text run with no colour of its own inherits the box colour rather than reading as empty
	text: {
		label: 'Color',
		title: () => 'Text colour',
		icon: <Baseline size={TOOLBAR_ICON_SIZE} />,
		preview: (color) => ({ color }),
		read: ({ annotation, style }) => style.color ?? annotation.textColor ?? DEFAULT_ANNOTATION_INK,
		write: ({ color, applyPatch }) => applyPatch({ color })
	},
	highlight: {
		label: 'Highlight',
		title: () => 'Highlight the selected text',
		icon: <Highlighter size={TOOLBAR_ICON_SIZE} />,
		noneLabel: () => 'No highlight',
		read: ({ style }) => style.backgroundColor ?? null,
		write: ({ color, applyPatch }) => applyPatch({ backgroundColor: color })
	},
	fill: {
		label: 'Color',
		title: (annotation) => (annotation.type === SkuAnnotationType.TextBox ? 'Text box fill' : 'Shape fill'),
		icon: <PaintBucket size={TOOLBAR_ICON_SIZE} />,
		noneLabel: () => 'No fill',
		preview: (color) => ({ fillColor: color }),
		read: ({ annotation }) => (isAnnotationFilled(annotation) ? annotation.backgroundColor || DEFAULT_ANNOTATION_FILL : null),
		write: ({ annotation, color }) => setAnnotationFill({ annotation, color })
	},
	stroke: {
		label: 'Color',
		title: (annotation) => (getAnnotationCapabilities(annotation.type).fill ? 'Border colour' : 'Line colour'),
		icon: <SquareDashed size={TOOLBAR_ICON_SIZE} />,
		noneLabel: (annotation) => (getAnnotationCapabilities(annotation.type).fill ? 'No border' : 'No line'),
		preview: (color) => ({ strokeColor: color }),
		read: ({ annotation }) => (isAnnotationStroked(annotation) ? annotation.color || DEFAULT_ANNOTATION_INK : null),
		write: ({ annotation, color }) => setAnnotationStroke({ annotation, color })
	}
};

export type AnnotationPaintControlProps = {
	channel: AnnotationPaintChannel;
	annotation: SkuAnnotation;
	style: EffectiveTextStyle;
	applyPatch: (patch: StyleRunPatch) => void;
	onPreview: (patch: AnnotationPreviewPatch | null) => void;
	isOpen: boolean;
	onOpenChange: (isOpen: boolean) => void;
};

/**
 * The ONE control every paint channel uses. Fill, border/line, text and highlight differ only by their
 * caption and which field they read and write -- both of which come from the channel descriptor, so a
 * capability added here reaches all four at once.
 */
export const AnnotationPaintControl = (props: AnnotationPaintControlProps): React.ReactElement => {
	const { channel, annotation, style, applyPatch, onPreview, isOpen, onOpenChange } = props;
	const { pushRecentColor } = useSkuAnnotation();
	const { label, title, icon, noneLabel, preview, read, write } = ANNOTATION_PAINT_CHANNELS[channel];

	const handleChange = (color: string | null): void => {
		if (color !== null) pushRecentColor(color);
		write({ annotation, color, applyPatch });
	};

	return (
		<AnnotationColorPopover
			title={title(annotation)}
			label={label}
			icon={icon}
			value={read({ annotation, style })}
			noneLabel={noneLabel?.(annotation)}
			onChange={handleChange}
			onPreview={preview ? (color) => onPreview(color ? preview(color) : null) : undefined}
			isOpen={isOpen}
			onOpenChange={onOpenChange}
		/>
	);
};
